import React from 'react';
import { Phone, Mail, MapPin, ExternalLink, Hand, Lock, CheckCircle2, AlertTriangle, RotateCcw, FilePlus2, PhoneOutgoing } from 'lucide-react';
import { RealtimeLead, LeadState, getSource, claimTimeRemaining, formatDuration, timeSinceContact } from '../data/realtimeLeads';
import { Badge } from './Badge';
import { Button } from './Button';
interface RealtimeLeadCardProps {
  lead: RealtimeLead;
  state: LeadState;
  now: number;
  onClaim: (id: string) => void;
  onCreateLoan: (id: string) => void;
  onRelease: (id: string) => void;
}
const ACCENT: Record<LeadState, string> = {
  available: 'border-l-4 border-l-blue-500',
  claimed_by_me: 'border-l-4 border-l-amber-500',
  claimed_by_other: 'border-l-4 border-l-slate-300',
  loan_created: 'border-l-4 border-l-emerald-500'
};
export function RealtimeLeadCard({
  lead,
  state,
  now,
  onClaim,
  onCreateLoan,
  onRelease
}: RealtimeLeadCardProps) {
  const source = getSource(lead.sourceId);
  const remaining = claimTimeRemaining(lead, now);
  // Under 4 hours left on a claim is the point where it needs attention.
  const expiringSoon = state === 'claimed_by_me' && remaining < 4 * 3600 * 1000;
  const formatCurrency = (amount: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(amount);
  const renderStatusColumn = () => {
    if (state === 'loan_created') {
      return <>
          <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700" data-id="element-211" data-mp="ejujg1m4ahd2">
            <CheckCircle2 size={12} data-id="element-212" data-mp="9l174cikgweh" /> Loan created
          </div>
          <div className="mt-2 text-sm text-slate-500" data-id="element-213" data-mp="5k7i1fte1bk0">
            Worked by {lead.claimedByName}
          </div>
        </>;
    }
    if (state === 'claimed_by_me') {
      return <>
          <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${expiringSoon ? 'border-red-200 bg-red-50 text-red-700' : 'border-amber-200 bg-amber-50 text-amber-700'}`} data-id="element-214" data-mp="3rrfnei4b5ig1">
            {expiringSoon ? <AlertTriangle size={12} data-id="element-215" data-mp="386fajvkjycmo" /> : <Hand size={12} data-id="element-216" data-mp="3d3pbo8hb0cqc" />}
            Claimed by you
          </div>
          <div className={`mt-2 text-2xl font-bold tabular-nums ${expiringSoon ? 'text-red-600' : 'text-slate-900'}`} data-id="element-217" data-mp="3d3xlfri4r1xb">
            {formatDuration(remaining)}
          </div>
          <div className="text-xs text-slate-500" data-id="element-218" data-mp="3r3ginp9eksuu">to create a loan</div>
        </>;
    }
    if (state === 'claimed_by_other') {
      return <>
          <div className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600" data-id="element-219" data-mp="3w0qhtwmlgivv">
            <Lock size={12} data-id="element-220" data-mp="sdq4404685o9" /> Claimed
          </div>
          <div className="mt-2 text-sm font-medium text-slate-700" data-id="element-221" data-mp="sfjadeavgnvq">
            {lead.claimedByName}
          </div>
          <div className="text-xs text-slate-500" data-id="element-222" data-mp="284sdgvkfc8c9">
            Frees up in {formatDuration(remaining)}
          </div>
        </>;
    }
    return <>
        <div className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-xs font-semibold text-blue-700" data-id="element-223" data-mp="2msvkm8a7dq2n">
          <Hand size={12} data-id="element-224" data-mp="1u47unzeiecir" /> Available
        </div>
        {lead.releaseCount > 0 && <div className="mt-2 inline-flex items-center gap-1 text-xs text-slate-500" data-id="element-225" data-mp="1p6g3x8plowg5">
            <RotateCcw size={11} data-id="element-226" data-mp="235fs6f97b9bs" />
            Returned {lead.releaseCount}&times;
          </div>}
      </>;
  };
  const renderActions = () => {
    if (state === 'loan_created') {
      return <Badge variant="success" className="px-3 py-1" data-id="element-227" data-mp="3ekbgksxngp5j">
          Converted
        </Badge>;
    }
    if (state === 'claimed_by_me') {
      return <>
          <Button variant="primary" size="sm" icon={<FilePlus2 size={16} data-id="element-229" data-mp="9puwi5vv949x" />} onClick={() => onCreateLoan(lead.id)} data-id="element-228" data-mp="3alltg8mt7t8p">
            Create Loan
          </Button>
          <Button variant="outline" size="sm" onClick={() => onRelease(lead.id)} title="Release back to the queue" data-id="element-230" data-mp="ioztv8voyrki">
            <RotateCcw size={16} data-id="element-231" data-mp="nlggtzr5mzw0" />
          </Button>
        </>;
    }
    if (state === 'claimed_by_other') {
      return <Button variant="outline" size="sm" disabled data-id="element-232" data-mp="wnzgyc3k2yek">
          Claimed
        </Button>;
    }
    return <Button variant="primary" size="sm" icon={<Hand size={16} data-id="element-234" data-mp="16eawyx0iky1t" />} onClick={() => onClaim(lead.id)} data-id="element-233" data-mp="11ey3htf50sq9">
        Claim Lead
      </Button>;
  };
  return <div className={`bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex flex-col lg:flex-row lg:items-center gap-4 ${ACCENT[state]} ${expiringSoon ? 'ring-1 ring-red-100' : ''}`} data-id="element-235" data-mp="1be6se7v5bh5r">
      {/* Claim status */}
      <div className="lg:w-40 shrink-0" data-id="element-236" data-mp="30sg31wx9n5c3">{renderStatusColumn()}</div>

      {/* Borrower */}
      <div className="flex-1 min-w-0" data-id="element-237" data-mp="2vu4v5rqj74wi">
        <div className="flex flex-wrap items-center gap-2" data-id="element-238" data-mp="2qw39cz1q0rhc">
          <h3 className="font-semibold" data-id="element-239" data-mp="2qwldsiyjec0c">
            {/* Links to the borrower's record page — not wired up in this prototype. */}
            <a href={`/leads/${lead.id}`} onClick={e => e.preventDefault()} title={`Open ${lead.name}'s record`} className="group inline-flex items-center gap-1 text-blue-600 underline decoration-blue-300 underline-offset-2 hover:text-blue-700 hover:decoration-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded-sm transition-colors" data-id="element-240" data-mp="3jkr2thwmin5z">
              {lead.name}
              <ExternalLink size={13} className="text-blue-400 group-hover:text-blue-600 transition-colors" data-id="element-241" data-mp="1fe31wao3ftj" />
            </a>
          </h3>
          {source && <Badge variant="info" data-id="element-242" data-mp="20mcmu9kv85s1">{source.name}</Badge>}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-sm text-slate-500" data-id="element-243" data-mp="20ljcyzqm105o">
          <span className="flex items-center gap-1.5" data-id="element-244" data-mp="1lx6gm1nm1fan">
            <Phone size={13} data-id="element-245" data-mp="1qvrgu12v3yz3" /> {lead.phone}
          </span>
          <span className="flex items-center gap-1.5 truncate" data-id="element-246" data-mp="1qu6kq70wg7z4">
            <Mail size={13} data-id="element-247" data-mp="1cvyqi6v8zkrn" /> {lead.email}
          </span>
          <span className="flex items-center gap-1.5" data-id="element-248" data-mp="1gy3hsyy845b4">
            <MapPin size={13} data-id="element-249" data-mp="1gzoht43b4ggd" /> {lead.state}
          </span>
        </div>
        <div className="flex items-center gap-1.5 mt-1.5 text-xs text-slate-500" data-id="element-250" data-mp="12zvt1ebccab1">
          <PhoneOutgoing size={12} className="text-slate-400" data-id="element-251" data-mp="17wvokvakdunw" />
          {lead.dialerOutcome} &middot; last contacted{' '}
          {formatDuration(timeSinceContact(lead, now))} ago
        </div>
      </div>

      {/* Loan */}
      <div className="lg:w-40 shrink-0" data-id="element-252" data-mp="32b6cn1jc4ksm">
        <div className="font-semibold text-slate-900" data-id="element-253" data-mp="qocnhnb4mfb9">
          {formatCurrency(lead.loanAmount)}
        </div>
        <div className="text-sm text-slate-500" data-id="element-254" data-mp="lprw8ddur5yy">{lead.loanPurpose}</div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 shrink-0" data-id="element-255" data-mp="10eo3i8834eyi">{renderActions()}</div>
    </div>;
}